import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import { formatMXN2 } from "@/lib/format";
import { ShoppingBag, ChevronRight } from "lucide-react";

export const ORDER_STATUS_META = {
    pending_payment: { label: "Pendiente de pago", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
    paid: { label: "Pagado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    label_generated: { label: "Guía generada", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    shipped: { label: "Enviado", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    delivered: { label: "Entregado", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    cancelled: { label: "Cancelado", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
    refunded: { label: "Reembolsado", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    out_of_stock: { label: "Sin stock", cls: "bg-red-50 text-red-700 border-red-200" },
};

export function StatusBadge({ status }) {
    const m = ORDER_STATUS_META[status] || ORDER_STATUS_META.pending_payment;
    return (
        <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border ${m.cls}`}>
            {m.label}
        </span>
    );
}

const FILTERS = [
    { v: "", label: "Todos" },
    { v: "paid", label: "Pagados" },
    { v: "label_generated", label: "Con guía" },
    { v: "shipped", label: "Enviados" },
    { v: "delivered", label: "Entregados" },
    { v: "out_of_stock", label: "Sin stock" },
];

export default function MerchantOrders() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async (st = "") => {
        setLoading(true);
        try {
            const { data: r } = await apiFetch.get("/merchant/orders", {
                params: { status: st || undefined, limit: 100 },
            });
            setData(r);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load(status);
    }, [status]);

    return (
        <div data-testid="merchant-orders" className="space-y-6">
            <header>
                <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Pedidos</h1>
                <p className="mt-2 text-sm text-zinc-600">
                    Los pedidos pagados están listos para que generes su guía de envío.
                </p>
            </header>

            <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map((f) => (
                    <button
                        key={f.v}
                        onClick={() => setStatus(f.v)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                            status === f.v
                                ? "bg-zinc-900 text-white border-zinc-900"
                                : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
                <span className="ml-auto text-xs text-zinc-400">{data.total} pedido(s)</span>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sm text-zinc-400">Cargando…</div>
                ) : data.items.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="mx-auto w-12 h-12 grid place-items-center rounded-full bg-zinc-100">
                            <ShoppingBag className="w-5 h-5 text-zinc-400" />
                        </div>
                        <p className="mt-3 text-sm text-zinc-500">Aún no tienes pedidos.</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-zinc-100">
                        {data.items.map((o) => {
                            const itemCount = (o.items || []).reduce((a, i) => a + i.quantity, 0);
                            return (
                                <li key={o.id}>
                                    <Link
                                        to={`/dashboard/orders/${o.id}`}
                                        data-testid={`merchant-order-row-${o.orderNumber}`}
                                        className="flex items-center gap-4 px-4 sm:px-5 py-4 hover:bg-zinc-50 transition"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-mono text-sm font-semibold text-zinc-900">{o.orderNumber}</span>
                                                <StatusBadge status={o.status} />
                                            </div>
                                            <div className="text-xs text-zinc-500 mt-0.5">
                                                {o.customerName} · {itemCount} artículo{itemCount !== 1 ? "s" : ""} · {o.deliveryMethod === "pickup" ? "Entrega acordada" : (o.shipping?.carrier || "—")}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-sm font-semibold text-zinc-900">{formatMXN2(o.amounts?.total)}</div>
                                            <div className="text-[11px] text-zinc-400">
                                                {o.createdAt ? new Date(o.createdAt).toLocaleDateString("es-MX") : ""}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-zinc-300 shrink-0" />
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
